import { Check, Plus, X } from "lucide-react";

import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
} from "ra-core";

import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Separator } from "@/components/ui/separator";

import type { Company, Contact, Tag } from "../types";

const CONTACT_TAG_NAMES = [
  "Заказчик",
  "Дизайнер",
  "Архитектор",
  "Подрядчик",
  "Монтаж",
  "Доставка",
  "Логистика",
  "Замер",
  "Производство",
  "Столяр",
  "Камень",
  "Стекло",
  "Металл",
  "Поставщик",
  "Фурнитура",
  "Материалы",
  "Оборудование",
  "Менеджер магазина",
];

const normalizeTagName = (value?: string | null) =>
  (value ?? "").trim().toLocaleLowerCase("ru-RU");

export const TaskContactPicker = ({
  value,
  onChange,
}: {
  value: Identifier[];
  onChange: (value: Identifier[]) => void;
}) => {
  const dataProvider = useDataProvider();

  const notify = useNotify();

  const { identity } = useGetIdentity();

  const [open, setOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);

  const [search, setSearch] = useState("");

  const [firstName, setFirstName] = useState("");

  const [lastName, setLastName] = useState("");

  const [phone, setPhone] = useState("");

  const [companyId, setCompanyId] = useState("");

  const [tagIds, setTagIds] = useState<number[]>([]);

  const [isSaving, setIsSaving] = useState(false);

  const { data: selectedContacts = [] } = useGetMany<Contact>(
    "contacts",
    {
      ids: value,
    },
    {
      enabled: value.length > 0,
    },
  );

  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: {
        page: 1,
        perPage: 30,
      },

      sort: {
        field: "last_name",
        order: "ASC",
      },

      filter: search.trim()
        ? {
            q: search.trim(),
          }
        : {},
    },
    {
      enabled: open && !createOpen,
    },
  );

  const { data: companies = [] } = useGetList<Company>(
    "companies",
    {
      pagination: {
        page: 1,
        perPage: 100,
      },

      sort: {
        field: "name",
        order: "ASC",
      },

      filter: {},
    },
    {
      enabled: createOpen,
    },
  );

  const { data: tags = [] } = useGetList<Tag>(
    "tags",
    {
      pagination: {
        page: 1,
        perPage: 100,
      },

      sort: {
        field: "name",
        order: "ASC",
      },

      filter: {},
    },
    {
      enabled: createOpen,
    },
  );

  const selectedIdSet = useMemo(() => new Set(value.map(String)), [value]);

  const availableContacts = contacts.filter(
    (contact) => !selectedIdSet.has(String(contact.id)),
  );

  const allowedTagNames = useMemo(
    () => new Set(CONTACT_TAG_NAMES.map(normalizeTagName)),
    [],
  );

  const contactTags = tags.filter((tag) =>
    allowedTagNames.has(normalizeTagName(tag.name)),
  );

  const addContact = (id: Identifier) => {
    if (selectedIdSet.has(String(id))) {
      return;
    }

    onChange([...value, id]);

    setSearch("");
    setOpen(false);
  };

  const removeContact = (id: Identifier) => {
    onChange(value.filter((currentId) => String(currentId) !== String(id)));
  };

  const toggleTag = (id: number) => {
    setTagIds((current) =>
      current.includes(id)
        ? current.filter((tagId) => tagId !== id)
        : [...current, id],
    );
  };

  const resetCreateForm = () => {
    setFirstName("");
    setLastName("");
    setPhone("");
    setCompanyId("");
    setTagIds([]);
    setCreateOpen(false);
  };

  const createContact = async () => {
    if (!firstName.trim()) {
      notify("Укажите имя контакта", {
        type: "warning",
      });

      return;
    }

    setIsSaving(true);

    try {
      const now = new Date().toISOString();

      const { data: contact } = await dataProvider.create("contacts", {
        data: {
          first_name: firstName.trim(),

          last_name: lastName.trim(),

          title: "",

          company_id: companyId ? Number(companyId) : null,

          email_jsonb: [],

          phone_jsonb: phone.trim()
            ? [
                {
                  number: phone.trim(),
                  type: "Work",
                },
              ]
            : [],

          first_seen: now,

          last_seen: now,

          has_newsletter: false,

          tags: tagIds,

          gender: "",

          sales_id: identity?.id,

          status: "new",

          background: "",
        } as any,
      });

      onChange([...value, contact.id]);

      resetCreateForm();

      setOpen(false);

      notify("Контакт создан и добавлен", {
        type: "success",
      });
    } catch (error) {
      console.error(error);

      notify("Не удалось создать контакт", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-sm font-medium">Контакты</div>

        <div className="mt-1 text-xs text-muted-foreground">
          Можно выбрать несколько человек
        </div>
      </div>

      {selectedContacts.length > 0 && (
        <div className="flex flex-col gap-2">
          {selectedContacts.map((contact) => {
            const phoneNumber = contact.phone_jsonb?.find((item) =>
              item.number?.trim(),
            )?.number;

            return (
              <div
                key={contact.id}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {[contact.first_name, contact.last_name]
                      .filter(Boolean)
                      .join(" ")}
                  </div>

                  {(contact.title || phoneNumber) && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {[contact.title, phoneNumber].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => removeContact(contact.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {value.length === 0 && (
        <div className="text-sm text-muted-foreground">Контакты не выбраны</div>
      )}

      {!open && (
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          onClick={() => {
            setOpen(true);

            setCreateOpen(false);
          }}
        >
          <Plus className="h-4 w-4" />
          Добавить контакт
        </Button>
      )}

      {open && (
        <div className="rounded-lg border p-3">
          {!createOpen ? (
            <div className="flex flex-col gap-3">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск контакта..."
                autoFocus
              />

              <div className="max-h-56 overflow-y-auto">
                {availableContacts.map((contact) => {
                  const phoneNumber = contact.phone_jsonb?.find((item) =>
                    item.number?.trim(),
                  )?.number;

                  return (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => addContact(contact.id)}
                      className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {[contact.first_name, contact.last_name]
                            .filter(Boolean)
                            .join(" ")}
                        </div>

                        {(contact.company_name ||
                          contact.title ||
                          phoneNumber) && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {[contact.company_name, contact.title, phoneNumber]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </div>

                      <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}

                {availableContacts.length === 0 && (
                  <div className="px-2 py-4 text-sm text-muted-foreground">
                    Ничего не найдено
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex flex-wrap justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setOpen(false);

                    setSearch("");
                  }}
                >
                  Закрыть
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Новый контакт
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-medium">Новый контакт</div>

                <div className="mt-1 text-xs text-muted-foreground">
                  Контакт сохранится в общем справочнике
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs text-muted-foreground">
                    Имя *
                  </div>

                  <Input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                </div>

                <div>
                  <div className="mb-1 text-xs text-muted-foreground">
                    Фамилия
                  </div>

                  <Input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs text-muted-foreground">
                  Телефон
                </div>

                <Input
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="+7..."
                />
              </div>

              <div>
                <div className="mb-1 text-xs text-muted-foreground">
                  Компания
                </div>

                <select
                  value={companyId}
                  onChange={(event) => setCompanyId(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Без компании</option>

                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {contactTags.length > 0 && (
                <div>
                  <div className="mb-2 text-xs text-muted-foreground">Роли</div>

                  <div className="flex flex-wrap gap-2">
                    {contactTags.map((tag) => {
                      const selected = tagIds.includes(tag.id);

                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                          className={
                            selected
                              ? "rounded-full ring-2 ring-foreground/20"
                              : "rounded-full opacity-75 hover:opacity-100"
                          }
                        >
                          <Badge
                            variant="secondary"
                            className="cursor-pointer gap-1.5 font-normal rovero-colored-tag text-[#482719]"
                            style={{
                              backgroundColor: tag.color,
                            }}
                          >
                            {selected && <Check className="h-3.5 w-3.5" />}

                            {tag.name}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  onClick={resetCreateForm}
                >
                  Назад
                </Button>

                <Button
                  type="button"
                  disabled={isSaving || !firstName.trim()}
                  onClick={createContact}
                >
                  Создать и добавить
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
